import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Library, Loader2, Trophy, Users, Search, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useLanguage } from '../contexts/LanguageContext';
import { fetchTournamentDecks, fetchMetaSummary } from '../lib/metaApi';
import { useRegion } from '../hooks/useRegion';
import RegionSwitcher from '../components/meta/RegionSwitcher';

const PAGE_SIZE = 24;

const PLACEMENTS = [
  ['', 'Any placement'],
  ['winners', 'Event winners'],
  ['top4', 'Top 4'],
  ['top8', 'Top 8'],
];

const SORTS = [
  ['recent', 'Most recent'],
  ['best', 'Best finish'],
  ['played', 'Most piloted'],
  ['converged', 'Most players'],
];

// Identical lists collapse to one tile by default. "shared" narrows to the lists
// more than one person independently arrived at — a small slice, and the only
// one where a combined win rate means anything.
const BUILDS = [
  ['unique', 'All builds'],
  ['shared', 'Shared builds only'],
];

function ordinal(n) {
  if (n === null || n === undefined) return '—';
  const rules = { one: 'st', two: 'nd', few: 'rd', other: 'th' };
  const suffix = rules[new Intl.PluralRules('en', { type: 'ordinal' }).select(n)] || 'th';
  return `${n}${suffix}`;
}

/**
 * The pilot's account name and their own free-text deck title are not served and
 * are not shown. The event, date and placement are — which means a row is still
 * traceable to a person against the published standings. That is understood and
 * accepted here, not overlooked.
 */
function DeckTile({ deck, onOpen, t }) {
  const best = deck.bestResult || {};
  // For a shared build the list's combined record is the honest one; for a
  // singleton the two are identical anyway.
  const record = (deck.sharedBuild && deck.group) || deck;
  // Falls back to bestRank for rows written before the pipeline emitted counts,
  // which is exactly what the old badge keyed on.
  const wins = record.top1s ?? (deck.bestRank === 1 ? 1 : 0);
  return (
    <Card
      className="cursor-pointer transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-ring"
      onClick={() => onOpen(deck.code)}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-2 mb-3">
          <h3 className="font-semibold leading-tight">{deck.archetype}</h3>
          {/* How many times it won, not merely whether it ever did. For a shared
              build that is the whole group's wins — a list five people took to
              four wins between them is a different result from one that won
              once, and "Won" collapsed both. */}
          {wins > 0 && (
            <Badge className="shrink-0 gap-1 tabular-nums">
              <Trophy className="h-3 w-3" />
              {wins === 1
                ? (t('deckDatabase.winOne') || '1 win')
                : t('deckDatabase.wins', { n: wins })}
            </Badge>
          )}
        </div>
        {/* Several people arriving at the same 50 cards independently is a
            different claim from one person's deck, so the record shown is the
            list's combined one rather than this registration's. */}
        {deck.sharedBuild && (
          <div className="flex items-center gap-1.5 mb-3 text-xs font-medium text-primary">
            <Users className="h-3.5 w-3.5" />
            {t('deckDatabase.sharedBuild', { n: deck.pilots })}
          </div>
        )}
        <dl className="grid grid-cols-2 gap-y-1.5 text-sm">
          <dt className="text-muted-foreground">{t('deckDatabase.bestFinish') || 'Best finish'}</dt>
          <dd className="text-right font-medium tabular-nums">
            {ordinal(deck.bestRank)}{best.field ? ` / ${best.field}` : ''}
          </dd>
          <dt className="text-muted-foreground">
            {deck.sharedBuild
              ? (t('deckDatabase.pilotedTotal') || 'Played')
              : (t('deckDatabase.piloted') || 'Piloted')}
          </dt>
          <dd className="text-right tabular-nums">
            {record.entries}× {record.events > 1 && `(${record.events} events)`}
          </dd>
          {deck.sharedBuild && (
            <>
              <dt className="text-muted-foreground">{t('deckDatabase.top4Rate') || 'Top 4 rate'}</dt>
              <dd className="text-right tabular-nums">
                {record.entries ? `${Math.round((record.top4s / record.entries) * 100)}%` : '—'}
              </dd>
            </>
          )}
          <dt className="text-muted-foreground">{t('deckDatabase.date') || 'Date'}</dt>
          <dd className="text-right tabular-nums">{best.endDateText || '—'}</dd>
        </dl>
        {best.eventName && (
          <p className="mt-3 pt-3 border-t text-xs text-muted-foreground line-clamp-1"
            title={best.eventName}>
            {best.eventName}
            {best.prefecture && ` · ${best.prefecture}`}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default function TournamentDecks() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const { region, setRegion, regions } = useRegion();

  const [result, setResult] = useState(null);
  const [archetypeOptions, setArchetypeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const archetype = searchParams.get('archetype') || '';
  const placement = searchParams.get('placement') || '';
  const sort = searchParams.get('sort') || 'recent';
  const builds = searchParams.get('builds') || 'unique';
  const card = searchParams.get('card') || '';
  const page = Math.max(parseInt(searchParams.get('page'), 10) || 1, 1);

  const [cardInput, setCardInput] = useState(card);
  useEffect(() => { setCardInput(card); }, [card]);

  // Filters live in the URL so a filtered view is linkable — the meta page's
  // "Decks" buttons deep-link straight into this list.
  const setParam = useCallback((updates) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(updates)) {
        if (value) next.set(key, value); else next.delete(key);
      }
      if (!('page' in updates)) next.delete('page');
      return next;
    });
  }, [setSearchParams]);

  // Archetype labels and card pools are region-specific, so a filter carried across
  // a region change would usually match nothing and read as "this region is empty".
  const changeRegion = useCallback((next) => {
    setParam({ archetype: '', card: '' });
    setRegion(next);
  }, [setParam, setRegion]);

  useEffect(() => {
    // Wait for the region to resolve, or the first request goes out against the
    // server default and is immediately superseded.
    if (region === undefined) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await fetchTournamentDecks({
          archetype, placement, sort, card, builds, page, pageSize: PAGE_SIZE,
          ...(region ? { region } : {}),
        });
        if (!cancelled) { setResult(data); setError(null); }
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [archetype, placement, sort, card, builds, page, region]);

  useEffect(() => {
    if (region === undefined) return undefined;
    let cancelled = false;
    (async () => {
      try {
        // Archetype names differ by region, so this list is region-scoped too.
        const summary = await fetchMetaSummary(region ? { region } : {});
        if (!cancelled) {
          setArchetypeOptions(summary.archetypes.filter(a => a.label !== 'Other').map(a => a.label));
        }
      } catch {
        /* the archetype dropdown is a convenience; the list works without it */
      }
    })();
    return () => { cancelled = true; };
  }, [region]);

  // Paging is driven by `hasMore` rather than a total. Serving a count made the
  // size of every filtered slice public, and an unfiltered call published the
  // size of the whole database.
  const canGoNext = Boolean(result?.hasMore) && page < (result?.maxPage ?? Infinity);

  const hasFilters = Boolean(archetype || placement || card || sort !== 'recent' || builds !== 'unique');

  return (
    <div className="min-h-[calc(100vh-48px-40px)] bg-gradient-to-b from-background to-muted/30">
      <Helmet>
        <title>Tournament Deck Database | Ultraman Card Game Tools</title>
        <meta
          name="description"
          content="Browse real Ultraman Card Game tournament decklists by archetype, placement and card, and load any of them into the deck builder."
        />
      </Helmet>

      <div className="bg-gradient-to-r from-brand to-brand-accent text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-3">
              <Library className="h-8 w-8" />
              <h1 className="text-3xl font-bold">
                {t('deckDatabase.title') || 'Tournament Deck Database'}
              </h1>
            </div>
            <RegionSwitcher
              region={region}
              regions={regions}
              onChange={changeRegion}
              variant="dark"
            />
          </div>
          <p className="text-white/80 max-w-2xl">
            {t('deckDatabase.subtitle') ||
              'Every published decklist from tracked tournaments. Filter by archetype, finish or a card you want to see played, then load any list straight into the deck builder.'}
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3 mb-6">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t('deckDatabase.filterArchetype') || 'Archetype'}
            </span>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm min-w-[200px]"
              value={archetype}
              onChange={(e) => setParam({ archetype: e.target.value })}
            >
              <option value="">{t('deckDatabase.allArchetypes') || 'All archetypes'}</option>
              {archetypeOptions.map((label) => (
                <option key={label} value={label}>{label}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t('deckDatabase.filterPlacement') || 'Placement'}
            </span>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={placement}
              onChange={(e) => setParam({ placement: e.target.value })}
            >
              {PLACEMENTS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t('deckDatabase.filterSort') || 'Sort'}
            </span>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={sort}
              onChange={(e) => setParam({ sort: e.target.value })}
            >
              {SORTS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-muted-foreground">
              {t('deckDatabase.filterBuilds') || 'Builds'}
            </span>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={builds}
              onChange={(e) => setParam({ builds: e.target.value })}
            >
              {BUILDS.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>

          <form
            className="flex flex-col gap-1"
            onSubmit={(e) => { e.preventDefault(); setParam({ card: cardInput.trim() }); }}
          >
            <span className="text-xs font-medium text-muted-foreground">
              {t('deckDatabase.filterCard') || 'Playing card'}
            </span>
            <div className="flex gap-1">
              <Input
                className="h-9 w-[160px]"
                placeholder="BP01-007"
                value={cardInput}
                onChange={(e) => setCardInput(e.target.value)}
              />
              <Button type="submit" size="sm" variant="secondary" className="h-9">
                <Search className="h-4 w-4" />
              </Button>
            </div>
          </form>

          {hasFilters && (
            <Button variant="ghost" size="sm" className="h-9"
              onClick={() => setSearchParams(new URLSearchParams())}>
              <X className="h-4 w-4 mr-1" />
              {t('deckDatabase.clear') || 'Clear'}
            </Button>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="py-16 text-center">
            <p className="text-muted-foreground">{error}</p>
          </div>
        ) : !result?.decks?.length ? (
          <div className="py-16 text-center">
            <Library className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
            <p className="text-muted-foreground">
              {t('deckDatabase.noResults') || 'No decklists match these filters.'}
            </p>
          </div>
        ) : (
          <>
            {(archetype || card) && (
              <p className="text-sm text-muted-foreground mb-4">
                {archetype}
                {archetype && card && ' · '}
                {card && <>{t('deckDatabase.playing') || 'playing'} {card}</>}
              </p>
            )}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {result.decks.map((deck) => (
                <DeckTile key={deck.code} deck={deck} t={t}
                  onOpen={(code) => navigate(
                    // The deck detail route looks the code up inside one region's
                    // partition, so the region has to come along.
                    region
                      ? `/decks/tournament/${code}?region=${encodeURIComponent(region)}`
                      : `/decks/tournament/${code}`
                  )} />
              ))}
            </div>

            {(page > 1 || canGoNext) && (
              <div className="flex items-center justify-center gap-2 mt-8">
                <Button variant="outline" size="sm" disabled={page <= 1}
                  onClick={() => setParam({ page: String(page - 1) })}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-muted-foreground tabular-nums px-2">
                  {page}
                </span>
                <Button variant="outline" size="sm" disabled={!canGoNext}
                  onClick={() => setParam({ page: String(page + 1) })}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
